import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle, Clock, AlertCircle, Download } from "lucide-react";

interface FullProcessProgressProps {
  videoId: string;
  status?: string;
  progress?: number;
  subtitleDone?: boolean;
  videoDone?: boolean;
}

export default function FullProcessProgress({ 
  videoId, 
  status: initialStatus, 
  progress: initialProgress = 0,
  subtitleDone = false,
  videoDone = false
}: FullProcessProgressProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(initialStatus || 'pending');
  const [progress, setProgress] = useState(initialProgress);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update local state when props change
  useEffect(() => {
    setStatus(initialStatus || 'pending');
    setProgress(initialProgress);
  }, [initialStatus, initialProgress]);

  // Auto-scroll when progress starts to show - COMMENTED OUT to prevent unwanted scrolling
  // useEffect(() => {
  //   if (status === 'full_processing') {
  //     setTimeout(() => {
  //       containerRef.current?.scrollIntoView({ 
  //         behavior: 'smooth', 
  //         block: 'start' 
  //       });
  //       // Add additional offset to prevent menu overlap
  //       window.scrollBy(0, -30);
  //     }, 100);
  //   }
  // }, [status]);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'processing':
        return 'Processing...';
      case 'full_processing':
        return 'Processing Full Video...';
      case 'completed':
        return 'Completed!';
      case 'error':
        return 'Error occurred';
      default:
        return 'Processing...';
    }
  };

  const getProgressColor = (status: string) => {
    switch (status) {
      case 'processing':
      case 'full_processing':
        return 'bg-blue-600';
      case 'completed':
        return 'bg-green-600';
      case 'error':
        return 'bg-red-600';
      default:
        return 'bg-blue-600';
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === 'completed') return <CheckCircle className="h-8 w-8 text-green-600" />;
    if (status === 'error') return <AlertCircle className="h-8 w-8 text-red-600" />;
    return <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />;
  };

  const getStatusColor = (status: string) => {
    if (status === 'completed') return 'text-green-600';
    if (status === 'error') return 'text-red-600';
    return 'text-blue-600';
  };

  const getProgressText = () => {
    if (status === 'completed' || status === 'completed') {
      return 'Your video with subtitles is ready to download';
    }
    if (status === 'error') {
      return 'Something went wrong. Please try again.';
    }
    if (subtitleDone && !videoDone) {
      return 'Subtitle ready! Creating video with embedded subtitles...';
    }
    if (progress >= 50 && progress < 80) {
      return 'Generating subtitles with AI...';
    }
    if (progress >= 90) {
      return 'Creating video with embedded subtitles...';
    }
    return `${progress}% complete`;
  };

  const getStepStatus = (step: string) => {
    if (status === 'completed' || status === 'completed') return 'completed';
    if (status === 'error') return 'error';
    
    switch (step) {
      case 'subtitle':
        return subtitleDone ? 'completed' : progress >= 60 ? 'processing' : 'pending';
      case 'video':
        return videoDone ? 'completed' : subtitleDone ? 'processing' : 'pending';
      default:
        return progress > 0 ? 'processing' : 'pending';
    }
  };

  const getStepProgress = (step: string) => {
    switch (step) {
      case 'subtitle':
        if (subtitleDone) return 100;
        if (progress >= 50 && progress < 80) {
          // Detailed subtitle progress
          return Math.min(progress, 80);
        }
        return Math.min(progress, 50);
      case 'video':
        if (videoDone) return 100;
        if (subtitleDone && progress >= 90) {
          return Math.max(progress - 80, 0);
        }
        return 0;
      default:
        return progress;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" ref={containerRef}>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          {getStatusIcon(status)}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {getStatusText(status)}
        </h3>
        <p className="text-gray-600 mb-4">
          {getProgressText()}
        </p>
        
        {/* Overall progress bar */}
        {status === 'full_processing' && (
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        )}
      </div>

      {/* Processing steps */}
      <div className="space-y-4">
        {/* Subtitle Generation Step */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              {getStepStatus('subtitle') === 'completed' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : getStepStatus('subtitle') === 'processing' ? (
                <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
              ) : (
                <Clock className="h-5 w-5 text-gray-300" />
              )}
              <span className="font-medium text-gray-900">Subtitle Generation</span>
            </div>
            <span className="text-sm text-gray-500">{getStepProgress('subtitle')}%</span>
          </div>
          <Progress value={getStepProgress('subtitle')} className="h-2" />
          {subtitleDone && (
            <div className="mt-2 flex items-center space-x-2 text-sm text-green-600">
              <Download className="h-4 w-4" />
              <span>Subtitle ready for download!</span>
            </div>
          )}
          {!subtitleDone && progress >= 50 && progress < 80 && (
            <div className="mt-2 space-y-1 text-xs text-blue-600">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Analyzing audio content</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Detecting speech patterns</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span>Generating timestamps</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span>Finalizing subtitle content</span>
              </div>
            </div>
          )}
        </div>

        {/* Video Creation Step */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              {getStepStatus('video') === 'completed' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : getStepStatus('video') === 'processing' ? (
                <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
              ) : (
                <Clock className="h-5 w-5 text-gray-300" />
              )}
              <span className="font-medium text-gray-900">Video with Embedded Subtitles</span>
            </div>
            <span className="text-sm text-gray-500">{getStepProgress('video')}%</span>
          </div>
          <Progress value={getStepProgress('video')} className="h-2" />
          {videoDone && (
            <div className="mt-2 flex items-center space-x-2 text-sm text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span>Video ready for download!</span>
            </div>
          )}
        </div>
      </div>

      {/* Download buttons when ready */}
      {subtitleDone && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3">
            <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <Download className="inline h-4 w-4 mr-2" />
              Download Subtitle (.srt)
            </button>
            {videoDone && (
              <button className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                <Download className="inline h-4 w-4 mr-2" />
                Download Video (.mp4)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
